// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What the current page is called, when only the page knows (S3-02).
//
// THE DEFECT THIS EXISTS FOR
// --------------------------
// `/play/neon-nova` and `/play/iron-fathom` both titled themselves `Play · HCC Pinball Arcade` and
// rendered the same heading, because the route table maps one pattern to one message key and a
// machine's name is not in the route table — it is in the definition, which arrives from the
// bundle, a cache or the network. So the two highest-intent pages on the site were one page to a
// crawler and to a bookmark, and the machine the visitor asked for was named nowhere in the
// document.
//
// WHY A SIGNAL
// ------------
// `PlayRoute` knows the machine and `AppShell` owns the head, and neither is the other's parent —
// the same shape `playFocus.ts` and `entitlementSignal.ts` already use, for the same reason. It is
// read through `useSyncExternalStore` (see `usePageMeta.ts`), which is the lesson M8-72 paid for:
// a child's mount effect runs before its parent's, so a `useState` in the shell would miss the
// value the page set on the way in.
//
// **It imports nothing**, like the two signals it copies, because the front door must not gain a
// module that could reach a network (FR-030).

/** What a page can tell the shell about itself. Every field replaces the route's default. */
export interface PageMeta {
  /** The whole `<title>`, brand included. */
  readonly title: string
  /** The `<meta name="description">`. */
  readonly description: string
  /** The page's `<h1>`, for the surfaces that render one. */
  readonly h1: string
}

type Listener = (meta: PageMeta | undefined) => void

let current: PageMeta | undefined
/** The address the metadata belongs to, so a stale page cannot name the next one. */
let owner: string | undefined
const listeners = new Set<Listener>()

/** What the current page calls itself, or nothing when it has not said. */
export function readPageMeta(): PageMeta | undefined {
  return current
}

/**
 * Say what this page is called.
 *
 * `pathname` is the address the metadata describes, and it is not optional: React unmounts the old
 * page after it mounts the new one in some transitions, so a bare `clear()` in a cleanup can
 * arrive *after* the next page has already spoken. Naming the owner makes the clear safe — it only
 * clears what it set.
 *
 * Idempotent by value, because the caller is an effect that re-runs whenever its machine's
 * definition object changes identity.
 */
export function setPageMeta(pathname: string, meta: PageMeta): void {
  if (
    owner === pathname &&
    current?.title === meta.title &&
    current.description === meta.description &&
    current.h1 === meta.h1
  ) {
    return
  }
  owner = pathname
  current = meta
  for (const listener of listeners) listener(current)
}

/** Forget what this address said, if it is still the one that said it. */
export function clearPageMeta(pathname: string): void {
  if (owner !== pathname) return
  owner = undefined
  current = undefined
  for (const listener of listeners) listener(undefined)
}

/** Watch it. Returns the unsubscribe. */
export function watchPageMeta(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
